/**
 * Monthly portfolio-metrics client (#17).
 *
 * Backend router: app/routers/agreement_aum.py, prefix /agreement-monthly-metrics.
 *   - GET  /{agreement_id}          the series, oldest month first (open read)
 *   - POST /                        upsert one month (Admin)
 *   - POST /import   (multipart)    bulk Excel import, mode=preview|commit (Admin)
 *   - GET  /template                blank .xlsx template (Admin)
 *
 * Money fields are integer cents; `return_ratio` is a decimal ratio (may arrive
 * as a string from PG, like Agreement.mifid_suitability_score). The multipart
 * import + the template download use a raw fetch (the JSON `api` client only
 * sends/receives JSON); the import sends `mode` and `on_conflict` as form
 * fields alongside the file (mirrors aqImports.ts). Template download streams a
 * blob and triggers a browser save via downloadBlob (mirrors iaaExports.ts).
 */
import { api } from './client';
import { downloadBlob } from './iaaExports';
import type { ISODate, ISODateTime, UUID } from './types';

/** One month's metrics for an agreement (GET / upsert response). */
export interface AgreementMonthlyMetric {
  id: UUID;
  agreement_id: UUID;
  period_month: ISODate;                  // normalised to first-of-month
  aum_eur_cents: number;                  // required — drives the row
  inflow_eur_cents: number | null;
  outflow_eur_cents: number | null;
  fee_ex_vat_cents: number | null;
  return_ratio: number | string | null;  // decimal ratio; PG Decimal → string
  source: string;                         // 'manual' | 'import'
  created_at: ISODateTime;
  updated_at: ISODateTime;
  created_by: UUID | null;
  updated_by: UUID | null;
}

/** Set/overwrite one month (POST body). aum_eur_cents required; rest optional. */
export interface AgreementMonthlyMetricUpsert {
  agreement_id: UUID;
  period_month: ISODate;
  aum_eur_cents: number;
  inflow_eur_cents?: number | null;
  outflow_eur_cents?: number | null;
  fee_ex_vat_cents?: number | null;
  return_ratio?: number | string | null;
  source?: string;                        // backend default 'manual'
}

export type MetricsImportMode = 'preview' | 'commit';
// 'replace' = make the file the COMPLETE historical record for its agreements: overwrite
// present months, add new ones, and DELETE stored historical months the file omits.
export type MetricsImportConflict = 'skip' | 'overwrite' | 'replace';

/** Per-field record counts for one action (New / Delete / Change) — a row of the
 *  change-preview table (columns AuM / Flows / Returns). */
export interface MetricFieldCounts {
  aum: number;
  flow: number;
  ret: number;
}

/** Result of a bulk import (preview or commit). On preview the
 *  inserted/overwritten/skipped/deleted counters stay 0 (nothing was written). */
export interface MetricsImportReport {
  mode: MetricsImportMode;
  on_conflict: MetricsImportConflict;
  total_rows: number;                     // parsed AuM-spine rows
  matched_ciifs: string[];
  unmatched_ciifs: string[];              // CIIF headers with no live agreement
  new_count: number;                      // (agreement, month) rows not present
  conflict_count: number;                 // exact count of pre-existing months
  conflict_sample: string[];              // ≤200 "CIIF-nnn YYYY-MM" — a SAMPLE
  delete_count: number;                   // stored historical months NOT in the file ('replace' deletes these)
  delete_sample: string[];                // ≤200 "CIIF-nnn YYYY-MM" — a SAMPLE of the deletions
  // Change-preview table (per field): New = records created; Delete = stored records 'replace'
  // removes; Change = existing records whose value ACTUALLY differs (incl. cleared to blank).
  new_by_field: MetricFieldCounts;
  deleted_by_field: MetricFieldCounts;
  changed_by_field: MetricFieldCounts;
  changed_count: number;                  // conflict rows with ≥1 field changed (≤ conflict_count)
  new_agreements: number;                 // distinct-agreement tallies for the summary line
  deleted_agreements: number;
  changed_agreements: number;
  errors: string[];                       // parse/value problems + cut-off rows (skipped)
  blocking_errors: string[];              // HARD data errors (negative magnitudes) — BLOCK import
  inserted: number;
  overwritten: number;
  skipped: number;
  deleted: number;                        // ('replace' commit) orphan months actually deleted
}

/** Pull the file name from a Content-Disposition header. Handles RFC 5987
 *  filename*=UTF-8'' first, then a plain filename="…" (the template endpoint
 *  sends the plain form). */
function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

export const monthlyMetricsApi = {
  getSeries: (agreementId: UUID) =>
    api.get<AgreementMonthlyMetric[]>(`/agreement-monthly-metrics/${agreementId}`),

  upsertMonth: (payload: AgreementMonthlyMetricUpsert) =>
    api.post<AgreementMonthlyMetric>('/agreement-monthly-metrics', payload),

  /** Hard-delete one stored month by row id (Admin; audit-logged). 204. */
  deleteMonth: (id: UUID) =>
    api.delete<void>(`/agreement-monthly-metrics/${id}`),

  /** Bulk import. preview parses + reconciles and writes nothing; commit
   *  applies (on_conflict 'overwrite' replaces existing months, 'skip' leaves
   *  them). The backend caches NOTHING between calls — the caller must keep the
   *  same File and re-send it on commit. */
  async importFile(
    file: File,
    mode: MetricsImportMode,
    onConflict: MetricsImportConflict,
  ): Promise<MetricsImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('on_conflict', onConflict);
    const res = await fetch('/agreement-monthly-metrics/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The metrics file could not be processed.'));
    }
    return res.json();
  },

  /** Download the blank, CIIF-columned upload template (.xlsx). */
  async downloadTemplate(): Promise<void> {
    const res = await fetch('/agreement-monthly-metrics/template', {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The template could not be generated.'));
    }
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'portfolio_metrics_template.xlsx'));
  },
};
